import { useCallback } from "react"
import * as PropTypes from "prop-types"
import Typography from "components/styled/Typography"

import useEmbla, { useEmblaArrows } from "hooks/useEmbla"
import Dots from "components/Slider/Dots"
import Arrow from "components/Slider/Arrow"
import { Viewport } from "components/styled/SliderBase"
import {
  ReviewsSection,
  ReviewCard,
  AuthorName,
  AuthorImage,
  ReviewDate,
  ReviewText,
  StarRating,
  HeaderContainer,
  SliderWrapper,
  SliderTrack,
  Slide,
  ControlsContainer
} from "./styles"

function StarIcon({ filled }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill={filled ? "#FFC107" : "#E0E0E0"}
      xmlns="http://www.w3.org/2000/svg">
      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
    </svg>
  )
}

StarIcon.propTypes = {
  filled: PropTypes.bool.isRequired
}

function GoogleIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .533 5.333.533 12S5.867 24 12.48 24c3.44 0 6.053-1.147 8.213-3.307C22.96 18.52 23.68 15.52 23.68 13.093c0-.987-.093-1.92-.253-2.173H12.48z"
        fill="#4285F4"
      />
    </svg>
  )
}

/**
 * @type import('embla-carousel').EmblaOptionsType
 */
const emblaOptions = { align: "start", containScroll: "trimSnaps" }

const breakpoints = [
  {
    size: "xl",
    slidesToScroll: 3,
    watchDrag: true
  },
  {
    size: "lg",
    slidesToScroll: 2,
    watchDrag: true
  }
]

function GoogleReviews({ initialData }) {
  const reviews = initialData?.reviews || []
  const rating = initialData?.rating || 0
  const totalReviews = initialData?.user_ratings_total || 0

  const { emblaRef, embla, selectedIndex, scrollSnaps } = useEmbla(emblaOptions, breakpoints)
  const { prevProps, nextProps } = useEmblaArrows(embla)
  const scrollTo = useCallback(index => embla && embla.scrollTo(index), [embla])

  if (!initialData || reviews.length === 0) {
    return null
  }

  const formatDate = timestamp =>
    new Date(timestamp * 1000).toLocaleDateString("es-ES", {
      year: "numeric",
      month: "long",
      day: "numeric"
    })

  return (
    <ReviewsSection>
      <HeaderContainer>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "10px" }}>
          <GoogleIcon />
          <Typography variant="title2" as="h2" style={{ marginLeft: "10px", display: "inline" }}>
            Opiniones de nuestros clientes
          </Typography>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" }}>
          <Typography variant="paragraph" style={{ fontWeight: "bold", marginRight: "5px" }}>
            {rating || "4.8"}
          </Typography>
          {[1, 2, 3, 4, 5].map(star => (
            <StarIcon key={star} filled={star <= Math.round(rating || 5)} />
          ))}
          {totalReviews > 0 && (
            <Typography variant="small" style={{ color: "#666", marginLeft: "5px" }}>
              ({totalReviews} reseñas)
            </Typography>
          )}
        </div>
      </HeaderContainer>

      <SliderWrapper>
        <ControlsContainer>
          <Arrow side="left" {...prevProps} />
          <Arrow side="right" {...nextProps} />
        </ControlsContainer>
        <Viewport ref={emblaRef}>
          <SliderTrack>
            {reviews.slice(0, 10).map((review, index) => (
              <Slide key={review.time || index}>
                <ReviewCard>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "12px"
                    }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {review.profile_photo_url && (
                        <AuthorImage
                          src={review.profile_photo_url}
                          alt={review.author_name}
                          referrerPolicy="no-referrer"
                        />
                      )}
                      <AuthorName>{review.author_name}</AuthorName>
                    </div>
                    <StarRating>
                      {[1, 2, 3, 4, 5].map(star => (
                        <StarIcon key={star} filled={star <= review.rating} />
                      ))}
                    </StarRating>
                  </div>

                  <ReviewText>&quot;{review.text}&quot;</ReviewText>

                  <ReviewDate>{formatDate(review.time)}</ReviewDate>
                </ReviewCard>
              </Slide>
            ))}
          </SliderTrack>
        </Viewport>
        <Dots selectedIndex={selectedIndex} snaps={scrollSnaps} handleClick={scrollTo} />
      </SliderWrapper>
    </ReviewsSection>
  )
}

GoogleReviews.defaultProps = {
  initialData: null
}

GoogleReviews.propTypes = {
  initialData: PropTypes.object
}

export default GoogleReviews
